/**
 * Cross-domain identity bridge between previously-marketing tryvotely.com and
 * votelyquiz.com (product). Both hosts run the same Next.js app and fire
 * to the same PostHog project, but the `distinct_id` cookie is scoped to
 * each origin, so a CTA click from marketing → product looks like two
 * unrelated anonymous users in PostHog.
 *
 * Fix: marketing CTAs append `?ph_did=<source_id>` to their target. On
 * the product host we read that param at app boot and alias the new
 * anonymous person to the source id so prior marketing-side events stitch
 * to the buyer.
 */
import type { PostHog } from 'posthog-js';

export const PH_DID_PARAM = 'ph_did';
const MARKETING_HOSTS = new Set(['tryvotely.com', 'www.tryvotely.com']);
const PRODUCT_ORIGIN = 'https://votelyquiz.com';

/**
 * Build a URL pointing at the product host with the current PostHog
 * distinct_id forwarded as a query param. When called from a host other
 * than the marketing site (localhost dev, votelyquiz.com itself), returns
 * the relative path unchanged so Next.js can client-route to it.
 */
export function buildCrossDomainQuizUrl(
  relativePath: string,
  posthog: PostHog | undefined | null,
): string {
  if (typeof window === 'undefined') return relativePath;
  const host = window.location.host.toLowerCase();
  if (!MARKETING_HOSTS.has(host)) return relativePath;

  const did = posthog?.get_distinct_id?.();
  const target = new URL(relativePath, PRODUCT_ORIGIN);
  if (did) target.searchParams.set(PH_DID_PARAM, did);
  return target.toString();
}

/**
 * Append the PostHog distinct_id to an already-absolute votelyquiz.com URL.
 * Used by marketing CTAs that hardcode the product origin so SSR'd markup
 * is correct even before hydration (Footer, GroupQuizSection). On any host
 * other than tryvotely.com the URL is returned unchanged.
 */
export function withCrossDomainDistinctId(
  absoluteUrl: string,
  posthog: PostHog | undefined | null,
): string {
  if (typeof window === 'undefined') return absoluteUrl;
  const host = window.location.host.toLowerCase();
  if (!MARKETING_HOSTS.has(host)) return absoluteUrl;

  const did = posthog?.get_distinct_id?.();
  if (!did) return absoluteUrl;
  try {
    const target = new URL(absoluteUrl);
    target.searchParams.set(PH_DID_PARAM, did);
    return target.toString();
  } catch {
    return absoluteUrl;
  }
}

/**
 * Consume a ph_did query param on the product host: identify the current
 * anonymous person as that id, then strip the param from the URL so it
 * doesn't ride along to the next page or leak via the Referer header.
 * Idempotent within a session via a sessionStorage guard, since the
 * PostHogProvider re-runs on every soft route change.
 */
export function consumeCrossDomainDistinctId(posthog: PostHog | undefined | null) {
  if (typeof window === 'undefined' || !posthog) return;
  const params = new URLSearchParams(window.location.search);
  const incoming = params.get(PH_DID_PARAM);
  if (!incoming) return;

  const STITCH_KEY = `ph_did_stitched:${incoming}`;
  try {
    if (sessionStorage.getItem(STITCH_KEY)) {
      // Already stitched in this session; just strip the param.
      stripParamFromUrl();
      return;
    }
    sessionStorage.setItem(STITCH_KEY, '1');
  } catch {
    // Private-mode browsers can throw on sessionStorage; fall through.
  }

  try {
    // alias() ties the incoming anon id (from tryvotely.com) to whatever
    // distinct_id PostHog currently has on this origin. After this, any
    // events on the marketing side and the product side roll up to one
    // PostHog person.
    posthog.alias(incoming);
  } catch {
    // never block the page on analytics
  }

  stripParamFromUrl();
}

function stripParamFromUrl() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(PH_DID_PARAM);
    const next = url.pathname + (url.search || '') + (url.hash || '');
    window.history.replaceState(window.history.state, '', next);
  } catch {
    // ignore
  }
}
